<template>
  <div class="music-monetization-landing">
    <section class="hero-section">
      <div class="container hero-content">
        <div class="hero-text">
          <h1 class="main-title">
            <span class="gradient-text">{{ $t('monetization.hero.title') }}</span>
          </h1>
          <p class="subtitle">
            {{ $t('monetization.hero.subtitle') }}
          </p>
          <BaseButton
            class="cta-btn mt-4"
            :to="'/upgrade?from=v4_landing-monetization'"
            variant="primary"
            is-gradient
            is-3d
            size="large"
          >
            <i class="fas fa-rocket mr-2" /> {{ $t('monetization.hero.cta') }}
          </BaseButton>
        </div>
      </div>
    </section>

    <section class="how-it-works-section">
      <div class="container">
        <h2 class="section-title mb-5">
          {{ $t('monetization.how.title') }}
        </h2>
        <div class="steps-grid">
          <div class="step-card">
            <div class="step-icon">
              <i class="fas fa-upload" />
            </div>
            <h3>{{ $t('monetization.how.step1.title') }}</h3>
            <p>{{ $t('monetization.how.step1.desc') }}</p>
          </div>
          <div class="step-card">
            <div class="step-icon">
              <i class="fas fa-broadcast-tower" />
            </div>
            <h3>{{ $t('monetization.how.step2.title') }}</h3>
            <p>{{ $t('monetization.how.step2.desc') }}</p>
          </div>
          <div class="step-card">
            <div class="step-icon">
              <i class="fas fa-coins" />
            </div>
            <h3>{{ $t('monetization.how.step3.title') }}</h3>
            <p>{{ $t('monetization.how.step3.desc') }}</p>
          </div>
          <div class="step-card">
            <div class="step-icon">
              <i class="fas fa-wallet" />
            </div>
            <h3>{{ $t('monetization.how.step4.title') }}</h3>
            <p>{{ $t('monetization.how.step4.desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="why-indiefy-section">
      <div class="container">
        <h2 class="section-title mb-5">
          {{ $t('monetization.why.title') }}
        </h2>
        <div class="features-grid">
          <div class="feature-card">
            <i class="fas fa-globe-americas feature-icon" />
            <h3>{{ $t('monetization.why.global') }}</h3>
            <p>{{ $t('monetization.why.global_desc') }}</p>
          </div>
          <div class="feature-card">
            <i class="fas fa-chart-line feature-icon" />
            <h3>{{ $t('monetization.why.analytics') }}</h3>
            <p>{{ $t('monetization.why.analytics_desc') }}</p>
          </div>
          <div class="feature-card">
            <i class="fas fa-bolt feature-icon" />
            <h3>{{ $t('monetization.why.payments') }}</h3>
            <p>{{ $t('monetization.why.payments_desc') }}</p>
          </div>
          <div class="feature-card">
            <i class="fas fa-user-shield feature-icon" />
            <h3>{{ $t('monetization.why.artistfirst') }}</h3>
            <p>{{ $t('monetization.why.artistfirst_desc') }}</p>
          </div>
        </div>
        <div class="cta-center mt-6">
          <BaseButton
            class="cta-btn"
            :to="'/upgrade?from=v4_landing-monetization'"
            variant="primary"
            is-gradient
            is-3d
            size="large"
          >
            <i class="fas fa-rocket mr-2" /> {{ $t('monetization.cta') }}
          </BaseButton>
        </div>
      </div>
    </section>

    <section class="seo-faq-section">
      <div class="container">
        <h2 class="section-title mb-5">
          {{ $t('monetization.faq.title') }}
        </h2>
        <div class="faq-list">
          <div class="faq-item">
            <h4>{{ $t('monetization.faq.q1') }}</h4>
            <p>{{ $t('monetization.faq.a1') }}</p>
          </div>
          <div class="faq-item">
            <h4>{{ $t('monetization.faq.q2') }}</h4>
            <p>{{ $t('monetization.faq.a2') }}</p>
          </div>
          <div class="faq-item">
            <h4>{{ $t('monetization.faq.q3') }}</h4>
            <p>{{ $t('monetization.faq.a3') }}</p>
          </div>
          <div class="faq-item">
            <h4>{{ $t('monetization.faq.q4') }}</h4>
            <p>{{ $t('monetization.faq.a4') }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseButton from '@/components/ui/BaseButton.vue';
import { usePageSeo } from '@/composables/usePageSeo';

const { t } = useI18n();

usePageSeo({
  titleKey: 'monetization.seo.title',
  descriptionKey: 'monetization.seo.description',
  keywords: computed(() => t('monetization.seo.keywords')),
});
</script>

<style scoped>
.music-monetization-landing {
  background: var(--color-background, #f9fafb);
}
.hero-section {
  background: linear-gradient(90deg, #3e8ed0 0%, #7c3aed 100%);
  color: #fff;
  padding: 4rem 0 2rem 0;
  position: relative;
}
.hero-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 2rem;
}
.hero-text {
  flex: 1 1 400px;
  max-width: 600px;
}
.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.25rem;
}
.gradient-text {
  background: linear-gradient(90deg, #a259f7, #3e8ed0 80%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 16px rgba(30, 41, 59, 0.28), 0 1px 0 #fff;
}
.subtitle {
  font-size: 1.25rem;
  margin-bottom: 2rem;
  color: #e0e7ef;
}
.hero-image {
  flex: 1 1 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-image img {
  max-width: 420px;
  width: 100%;
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(60,60,100,0.18);
}
@media (max-width: 900px) {
  .hero-content {
    flex-direction: column;
    align-items: flex-start;
  }
  .hero-image {
    margin-top: 2rem;
    width: 100%;
    justify-content: flex-start;
  }
}
.section-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text, #1e293b);
  margin-bottom: 2rem;
}
.how-it-works-section {
  background: #fff;
  padding: 4rem 0 2rem 0;
}
.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2rem;
}
.step-card {
  background: #f3f4f6;
  border-radius: 14px;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(60,60,100,0.04);
}
.step-icon {
  font-size: 2.5rem;
  color: #7c3aed;
  margin-bottom: 1rem;
}
.why-indiefy-section {
  background: var(--color-background, #f9fafb);
  padding: 4rem 0 2rem 0;
}
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2rem;
}
.feature-card {
  background: #fff;
  border-radius: 14px;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(60,60,100,0.04);
}
.feature-icon {
  font-size: 2.5rem;
  color: #3e8ed0;
  margin-bottom: 1rem;
}
.cta-center {
  text-align: center;
}
.cta-btn {
  font-size: 1.15rem;
  padding: 1rem 2.5rem;
  border-radius: 32px;
  font-weight: 700;
  letter-spacing: 1px;
}
.seo-faq-section {
  background: #fff;
  padding: 4rem 0 2rem 0;
}
.faq-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}
.faq-item {
  background: #f3f4f6;
  border-radius: 12px;
  padding: 1.5rem 1.25rem;
  box-shadow: 0 2px 8px rgba(60,60,100,0.04);
}
.faq-item h4 {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
@media (max-width: 900px) {
  .faq-list {
    grid-template-columns: 1fr;
  }
  .features-grid, .steps-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 768px) {
  .hero-section {
    padding: 1.5rem 0 1.2rem 0;
    border-radius: 0 0 24px 24px;
    box-shadow: 0 4px 24px rgba(60,60,100,0.10);
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #3e8ed0 0%, #7c3aed 100%);
  }
  .hero-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0;
  }
  .hero-text {
    max-width: 100%;
    width: 100%;
    padding: 0 0.5rem;
  }
  .main-title {
    font-size: 1.35rem;
    margin-bottom: 0.85rem;
    line-height: 1.2;
  }
  .subtitle {
    font-size: 1.02rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
  .cta-btn {
    width: 100%;
    padding: 1.15rem 0;
    font-size: 1.15rem;
    margin-bottom: 0.5rem;
    box-shadow: 0 2px 12px rgba(62, 142, 208, 0.18);
  }
  .section-title {
    font-size: 1.25rem;
    margin-bottom: 1.25rem;
  }
  .how-it-works-section,
  .why-indiefy-section,
  .seo-faq-section {
    padding: 2rem 0 1rem 0;
  }
  .step-card,
  .feature-card,
  .faq-item {
    padding: 1.25rem 0.75rem;
    font-size: 0.97rem;
  }
  .step-icon,
  .feature-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
  .faq-list,
  .features-grid,
  .steps-grid {
    gap: 1.25rem;
  }
  .cta-center {
    margin-top: 2rem;
  }
}
</style>